import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../theme/useChartTokens';
import { formatMusicalKey } from '../../utils/trackFormatting';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

interface TrackAudioFeatures {
  tempo: number | null;
  musicalKey: number | null;
  mode: number | null;
  energy: number | null;
  danceability: number | null;
  valence: number | null;
  acousticness: number | null;
  instrumentalness: number | null;
  liveness: number | null;
  speechiness: number | null;
}

interface TrackAudioProfileCardProps {
  audioFeatures: TrackAudioFeatures | null;
  popularity: number | null;
}

const TrackAudioProfileCardComponent: React.FC<TrackAudioProfileCardProps> = ({ audioFeatures, popularity }) => {
  const intl = useIntl();
  const tokens = useChartTokens();

  const radarData = useMemo(() => {
    if (!audioFeatures) {
      return [];
    }
    const axes: { key: keyof TrackAudioFeatures; label: string }[] = [
      {
        key: 'energy',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.energy', defaultMessage: 'Energy' }),
      },
      {
        key: 'danceability',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.dance', defaultMessage: 'Dance' }),
      },
      {
        key: 'valence',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.valence', defaultMessage: 'Mood' }),
      },
      {
        key: 'acousticness',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.acoustic', defaultMessage: 'Acoustic' }),
      },
      {
        key: 'instrumentalness',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.instrumental', defaultMessage: 'Instrumental' }),
      },
      {
        key: 'liveness',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.live', defaultMessage: 'Live' }),
      },
      {
        key: 'speechiness',
        label: intl.formatMessage({ id: 'app.sml.track.details.audio.speech', defaultMessage: 'Speech' }),
      },
    ];
    return axes.map((a) => ({
      axis: a.label,
      value: Math.round(((audioFeatures[a.key] as number | null) ?? 0) * 100),
    }));
  }, [audioFeatures, intl]);

  if (!audioFeatures) {
    return null;
  }

  const keyLabel = formatMusicalKey(audioFeatures.musicalKey, audioFeatures.mode);

  return (
    <Panel className="h-100">
      <PanelHead
        titleAs="h2"
        title={intl.formatMessage({ id: 'app.sml.track.details.audio.title', defaultMessage: 'Audio profile' })}
      />
      <PanelBody>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData}>
              <PolarGrid stroke={tokens.grid} />
              <PolarAngleAxis dataKey="axis" tick={{ fill: tokens.label, fontSize: 11 }} />
              <Radar dataKey="value" stroke={tokens.pos} fill={tokens.pos} fillOpacity={0.4} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="d-flex flex-wrap gap-3 mt-2 small text-muted">
          {audioFeatures.tempo !== null && (
            <span>
              {intl.formatMessage(
                { id: 'app.sml.track.details.audio.bpm', defaultMessage: 'BPM {v}' },
                { v: Math.round(audioFeatures.tempo) }
              )}
            </span>
          )}
          {keyLabel !== null && (
            <span>
              {intl.formatMessage(
                { id: 'app.sml.track.details.audio.key', defaultMessage: 'Key {v}' },
                { v: keyLabel }
              )}
            </span>
          )}
          {popularity !== null && (
            <span>
              {intl.formatMessage(
                { id: 'app.sml.track.details.audio.popularity', defaultMessage: 'Popularity {v}' },
                { v: popularity }
              )}
            </span>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const TrackAudioProfileCard = TypedMemo(TrackAudioProfileCardComponent);
